import { ActivateCardButton } from '@/src/common/components/ActivateCardButton'
import { CardDeliveryStatus } from '@/src/common/components/CardDeliveryStatus'
import { MesaFooter } from '@/src/common/components/MesaFooter'
import { PartnerDisclosures } from '@/src/common/components/PartnerDisclosures'
// Refer a Friend temporarily hidden - see https://mesamember.slack.com/archives/C07JK5U3H8S/p1764867423105289
// import { ReferAFriendButton } from '@/src/common/components/Referrals/ReferAFriendButton'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import {
  GET_CARD_ORDER_INFORMATION,
  GET_CREDIT_CARD_INFORMATION,
} from '@/src/common/gql/queries/card'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useSourcePartnerName } from '@/src/common/hooks/useSourcePartnerName'
import { CardSection } from '@/src/common/modules/CardSection'
import { MortgageModule } from '@/src/common/modules/MortgageModule'
import { MainWrapper } from '@/src/common/wrappers/MainWrapper'
import { DesignSystem } from '@/src/design-system'
import { Experiences } from '@/src/experiences'
import { GET_CARD_APPLICATION_STATUS } from '@/src/experiences/card-application/gql/queries'
import { CardApplication } from '@/src/experiences/Experiences'
import { useContentCards } from '@/src/services/Braze/useContentCards'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import { View, useWindowDimensions } from 'react-native'
import { useMediaQuery } from 'react-responsive'
import { Fragment } from 'react/jsx-runtime'
import { HomeE2E } from '@/src/experiences/home/e2e'
import * as Mortgage from '@/src/experiences/mortgage'
import * as Components from '@/src/experiences/home/components'
import * as Statsig from '@/src/experiences/home/statsig'
import { ShutdownNotice } from '@/src/common/modules/ShutdownNotice'
import { Rewards } from '@/src/experiences/rewards'
import { SegmentDomainProvider } from '@/src/services/segment/SegmentDomainProvider'

export function HomeIndex() {
  const onError = useLogError()
  const mortgageModuleBehavior = Mortgage.Hooks.useMortgageModuleStateBehavior()
  const useNewMortgageSectionFeatureGate = useFeatureGate(Statsig.FeatureGate.UseNewMortgageSection)
  const isCardInTransitUiEnabledFeatureGate = useFeatureGate(FeatureGates.CardInTransitUiEnabled)
  const isCardInTransitUiEnabled = isCardInTransitUiEnabledFeatureGate.featureGate?.value
  const loadingIsCardInTransitUiEnabled =
    isCardInTransitUiEnabledFeatureGate.loadingStatus !== 'Ready'

  const { data: cardApplicationStatus, loading: loadingCardApplicationStatus } = useLoggedInQuery(
    GET_CARD_APPLICATION_STATUS,
    { fetchPolicy: 'cache-and-network', onError },
  )

  const { data: cardOrderData, loading: loadingCardOrderData } = useLoggedInQuery(
    GET_CARD_ORDER_INFORMATION,
    { fetchPolicy: 'cache-and-network', onError },
  )

  const { data: creditCardInformation, loading: loadingCreditCardInformation } = useLoggedInQuery(
    GET_CREDIT_CARD_INFORMATION,
    { fetchPolicy: 'cache-and-network', onError },
  )

  // SourcePartnerName are distribution partners
  const { sourcePartnerName, loading: loadingSourcePartnerName } = useSourcePartnerName()

  const cardOrderStatus = cardOrderData?.getCardOrderInformation.node?.status

  const cardOrderStatusInDisplayableStatus =
    cardOrderStatus === 'SUBMITTED' ||
    cardOrderStatus === 'PRINTED' ||
    cardOrderStatus === 'SHIPPED'

  const hasPhysicalCardToActivate =
    creditCardInformation?.getCreditCardInformation.node?.virtualCardIdForPhysicalCard

  const isCardIssued = ['ISSUED', 'APPROVAL_ACKED'].includes(
    cardApplicationStatus?.getCardProductApplicationStatus.status ?? '',
  )

  const loadingMortgageSection =
    mortgageModuleBehavior === 'loading' ||
    mortgageModuleBehavior === 'uninitialized' ||
    useNewMortgageSectionFeatureGate.loadingStatus !== 'Ready'

  // The list of partners are: https://github.com/Acre-Rewards/acre-rewards-api/blob/ab36f64781c9f98dec414bcf850bd821d1898b1a/prisma/schema.prisma#L84
  // Note: not all partners are closed loop, i.e. has a mortgage product we want to drive back their customers to.
  // If the parameter store, mesa_mortgage_module has a key-value for the partner name, then we want to display the mortgage module.
  const isClosedLoopPartner = sourcePartnerName !== 'default'

  const shouldShowMortgageSection =
    ((!loadingMortgageSection && mortgageModuleBehavior.type !== 'hide') ||
      (!loadingMortgageSection && isClosedLoopPartner)) &&
    !useNewMortgageSectionFeatureGate.featureGate.value

  const isSplitScreen = useMediaQuery({ minWidth: 720 })
  const minWidth = isSplitScreen ? '42%' : '88%'

  const shouldRenderCardOnboardingCallToAction =
    cardApplicationStatus?.getCardProductApplicationStatus.status !== 'ISSUED'

  const windowDimensions = useWindowDimensions()
  const minHeight = windowDimensions.height / (isSplitScreen ? 2 : 3)

  /** We only want to show the card delivery status if the user has a physical card issued and ordered */
  const cardDeliveryStatusModule = isCardInTransitUiEnabled &&
    hasPhysicalCardToActivate &&
    cardOrderStatusInDisplayableStatus && (
      <Fragment key="card-delivery-status-module">
        <CardDeliveryStatus
          trackingNumber={cardOrderData?.getCardOrderInformation.node?.trackingNumber}
          status={cardOrderStatus}
          style={{ width: '100%' }}
        />
        {cardOrderStatus === 'SHIPPED' && <ActivateCardButton style={{ width: '100%' }} />}
      </Fragment>
    )

  const modulesThatCanBeHalfWidth = [
    ...(isCardIssued
      ? [<ShutdownNotice style={{ flex: 1, minWidth }} key="shutdown-notice" />]
      : []),
    ...(shouldRenderCardOnboardingCallToAction
      ? [<CardSection style={{ flex: 1 }} key="card-section" />]
      : []),
    ...(shouldShowMortgageSection
      ? [<MortgageModule style={{ flex: 1, minWidth }} key="mortgage-module" />]
      : []),
    ...(useNewMortgageSectionFeatureGate.featureGate?.value === true
      ? [
          <View style={{ flex: 1, minWidth }} key="mortgage-section">
            <Components.MortgageSection />
          </View>,
        ]
      : []),
    ...[
      <View style={{ flex: 1, minWidth, gap: 32 }} key="rewards-header">
        <SegmentDomainProvider domain="Rewards">
          <Rewards.Components.BalanceHeaderCard />
          <Rewards.Components.Actions style={{ width: '100%' }} gap={16} />
        </SegmentDomainProvider>
      </View>,
    ],
  ] as const

  const contentCards = useContentCards('home')
  const contentCardModules = contentCards.map((contentCard) => (
    <DesignSystem.BrazeContentCard
      key={contentCard.id}
      minWidth={minWidth}
      contentCard={contentCard}
      style={{ alignSelf: isSplitScreen ? 'flex-start' : undefined }}
    />
  ))

  const modules = [
    ...(cardDeliveryStatusModule ? [cardDeliveryStatusModule] : []),
    ...contentCardModules,
    ...modulesThatCanBeHalfWidth,
  ] as const

  // Disclosures for co-branded partners
  const parsedResult = Mortgage.Statsig.Store.MortgageModuleVariantName.safeParse(sourcePartnerName)
  const parsedSourcePartnerName = parsedResult.success ? parsedResult.data : 'default'
  const moduleDesign = Mortgage.Statsig.Store.useMortgageModuleDesign(parsedSourcePartnerName)
  const { disclosure_paragraphs } = moduleDesign

  return (
    <MainWrapper
      screen="/main/home"
      footer={
        <>
          {disclosure_paragraphs && disclosure_paragraphs.length > 0 && (
            <PartnerDisclosures disclosureParagraphs={disclosure_paragraphs} />
          )}
          <MesaFooter style={{ width: '100%' }} />
        </>
      }
      scrollViewProps={{ testID: 'Main.Home.ScrollView' }}
      testID={HomeE2E.Ids.View.Highnote.HomeIndex}
    >
      <Experiences.Notifications.Components.EnablePermissionsBanner text="Always stay up to date with Mesa's card and mortgage rewards!" />
      {/* <ReferAFriendButton style={{ width: '100%' }} sourcePartnerName={sourcePartnerName} /> */}
      <CardApplication.Components.Highnote.ApplicationStatusBanner />
      {loadingIsCardInTransitUiEnabled ||
      loadingCardApplicationStatus ||
      loadingCardOrderData ||
      loadingCreditCardInformation ||
      loadingMortgageSection ||
      loadingSourcePartnerName
        ? // @todo implement shimmer/skeleton for each module instead of blocking all modules on all loading states
          [1, 2].map((_, index) => (
            <DesignSystem.Skeleton.Skeleton
              key={index}
              style={{
                width: 'auto',
                flex: 1,
                minWidth,
                borderRadius: 16,
                height: minHeight,
                overflow: 'hidden',
              }}
            />
          ))
        : modules}
    </MainWrapper>
  )
}
